/**
 * PropertyLinkBanner — Iter171.
 *
 * Surfaces a clear warning when an appraisal has no `property_id` set,
 * because every Roof Details / Builder / Components flow downstream
 * silently breaks without one (saves no-op, panels show empty).
 *
 * Provides three remedies inline so the operator can resolve without
 * leaving the appraisal:
 *   1. Search-and-pick an existing property (debounced /properties?search=).
 *   2. Quick-create a new property from a single address line and
 *      auto-link it on success.
 *   3. Jump to the Properties tab on the appraisal to set it via the
 *      existing PropertyTenantAgentCard.
 *
 * On successful link/create, calls onLinked() so the parent can
 * re-load the appraisal + fire roof-details-changed so every panel
 * picks the new property up immediately.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Search, Plus, Loader2, Home, Check } from 'lucide-react';
import { toast } from 'sonner';
import { fireRoofDetailsChanged } from '@/utils/roofEvents';

export default function PropertyLinkBanner({ api, appraisalId, appraisal, onLinked }) {
  const [mode, setMode] = useState('idle'); // 'idle' | 'search' | 'create'
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(false);
  const [newAddr, setNewAddr] = useState({ num: '', street: '', suburb: '', city: '' });
  const wrapRef = useRef(null);

  // Debounced property search
  useEffect(() => {
    if (mode !== 'search' || !query.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/properties?search=${encodeURIComponent(query)}&limit=8`);
        setResults(data?.items || []);
      } finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [api, query, mode]);

  const linkPropertyId = useCallback(async (propertyId, propertyDoc) => {
    if (!propertyId) return;
    setLinking(true);
    try {
      // Wave 2 H9 fix (2026-02) — TRUE partial PUT. Backend's
      // `update_appraisal` uses `model_dump(exclude_unset=True)` so
      // sending JUST `property_id` here preserves every other field
      // on the appraisal. The previous `{ ...(appraisal || {}), … }`
      // spread was silently sending ~40 stale fields on every link
      // call, which is exactly how H9 (subtype revert) manifested.
      await api.put(`/appraisals/${appraisalId}`, {
        property_id: propertyId,
      });
      toast.success(`Linked to ${propertyDoc?.property_address_num || ''} ${propertyDoc?.property_address_street || 'property'}`.trim());
      fireRoofDetailsChanged();
      onLinked?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to link property');
    } finally {
      setLinking(false);
    }
  }, [api, appraisalId, appraisal, onLinked]);

  const handleCreate = useCallback(async () => {
    if (!newAddr.street.trim()) {
      toast.error('Street is required to create a property');
      return;
    }
    setLinking(true);
    try {
      const { data } = await api.post('/properties', {
        client_id: appraisal?.client_id || '',
        property_address_num:    newAddr.num.trim(),
        property_address_street: newAddr.street.trim(),
        property_address_suburb: newAddr.suburb.trim(),
        property_address_city:   newAddr.city.trim(),
      });
      await linkPropertyId(data._id, data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create property');
      setLinking(false);
    }
  }, [api, appraisal, newAddr, linkPropertyId]);

  return (
    <div
      ref={wrapRef}
      data-testid="property-link-banner"
      className="border-l-4 border-amber-400 bg-amber-50 rounded-md px-4 py-3 space-y-2"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-900">
            This appraisal isn't linked to a property yet.
          </p>
          <p className="text-xs text-amber-800/80 mt-0.5">
            Roof Details, Builder calculations and Components all need a property record before they can save. Pick or create one to continue.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pl-7">
        <button
          type="button"
          data-testid="property-link-mode-search"
          onClick={() => { setMode(m => m === 'search' ? 'idle' : 'search'); setQuery(''); setResults([]); }}
          className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded border ${mode === 'search' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-amber-800 border-amber-300 hover:bg-amber-100'}`}
        >
          <Search size={12} /> Find existing property
        </button>
        <button
          type="button"
          data-testid="property-link-mode-create"
          onClick={() => setMode(m => m === 'create' ? 'idle' : 'create')}
          className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded border ${mode === 'create' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-amber-800 border-amber-300 hover:bg-amber-100'}`}
        >
          <Plus size={12} /> Create new property
        </button>
      </div>

      {mode === 'search' && (
        <div className="pl-7 space-y-1" data-testid="property-link-search-body">
          <div className="relative">
            <input
              autoFocus
              data-testid="property-link-search-input"
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search by address (e.g. 12 Smith St) or suburb…"
              className="w-full text-sm px-3 py-1.5 border border-amber-300 rounded bg-white"
            />
            {searching && <Loader2 size={12} className="absolute right-2 top-2.5 animate-spin text-amber-500" />}
          </div>
          {results.length > 0 && (
            <div className="border border-amber-200 rounded bg-white max-h-48 overflow-y-auto">
              {results.map(p => {
                const line = `${p.property_address_num || ''} ${p.property_address_street || ''}, ${p.property_address_suburb || ''}`.trim().replace(/^,\s*/, '');
                return (
                  <button
                    key={p._id}
                    type="button"
                    data-testid={`property-link-result-${p._id}`}
                    disabled={linking}
                    onClick={() => linkPropertyId(p._id, p)}
                    className="w-full text-left text-sm px-3 py-1.5 hover:bg-amber-50 border-b border-amber-100 last:border-0 flex items-center gap-2 disabled:opacity-50"
                  >
                    <Home size={12} className="text-amber-700" />
                    <span className="flex-1">{line || '(no address)'}</span>
                    <Check size={12} className="text-emerald-700" />
                  </button>
                );
              })}
            </div>
          )}
          {query && !searching && results.length === 0 && (
            <p className="text-[11px] italic text-amber-700">
              No property matches “{query}”. Try the <strong>Create new property</strong> option instead.
            </p>
          )}
        </div>
      )}

      {mode === 'create' && (
        <div className="pl-7 space-y-2" data-testid="property-link-create-body">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <input
              data-testid="property-link-create-num"
              value={newAddr.num}
              onChange={e => setNewAddr(a => ({ ...a, num: e.target.value }))}
              placeholder="No"
              className="text-sm px-3 py-1.5 border border-amber-300 rounded bg-white"
            />
            <input
              data-testid="property-link-create-street"
              value={newAddr.street}
              onChange={e => setNewAddr(a => ({ ...a, street: e.target.value }))}
              placeholder="Street *"
              className="text-sm px-3 py-1.5 border border-amber-300 rounded bg-white sm:col-span-2"
            />
            <input
              data-testid="property-link-create-suburb"
              value={newAddr.suburb}
              onChange={e => setNewAddr(a => ({ ...a, suburb: e.target.value }))}
              placeholder="Suburb"
              className="text-sm px-3 py-1.5 border border-amber-300 rounded bg-white"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              data-testid="property-link-create-city"
              value={newAddr.city}
              onChange={e => setNewAddr(a => ({ ...a, city: e.target.value }))}
              placeholder="City"
              className="flex-1 text-sm px-3 py-1.5 border border-amber-300 rounded bg-white"
            />
            <button
              type="button"
              data-testid="property-link-create-submit"
              disabled={linking || !newAddr.street.trim()}
              onClick={handleCreate}
              className="text-xs font-bold px-3 py-1.5 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40 inline-flex items-center gap-1"
            >
              {linking ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
              Create & link
            </button>
          </div>
          <p className="text-[10px] text-amber-700/70">
            Only <strong>Street</strong> is required to create. You can fill in pitch, m², roof type etc. on the Roof Details tab right after.
          </p>
        </div>
      )}
    </div>
  );
}
